import React, { useState, useEffect } from "react";
import {
    getPromoCodes,
    createPromoCode,
    updatePromoCode,
    deletePromoCode,
    togglePromoCodeStatus
} from "../../../../firebase/promoCode-functions";
import { updateStoreSettings } from "../../../../firebase/settings-functions";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "../translations";
import "./PromoCodesTab.css";

const PromoCodesTab = ({ storeId, storeData, handleChange }) => {
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);
    const [promoCodes, setPromoCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [saving, setSaving] = useState(false);

    // Form state for creating/editing promo codes
    const [formData, setFormData] = useState({
        code: "",
        description: "",
        discountType: "percentage",
        discountValue: "",
        minimumOrderAmount: "",
        maximumDiscountAmount: "",
        usageLimit: "",
        isActive: true,
        validFrom: "",
        validUntil: "",
        applicableProducts: [],
        applicableCategories: [],
        firstTimeOnly: false
    });

    // Load promo codes on component mount
    useEffect(() => {
        if (storeId) {
            // console.log("PromoCodesTab - Loading promo codes for store:", storeId);
            loadPromoCodes();
        } else {
            // console.warn("PromoCodesTab - No storeId provided");
        }
    }, [storeId]);

    // Clear messages after delay
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    const loadPromoCodes = async () => {
        try {
            setLoading(true);
            setError("");

            // console.log("Loading promo codes for store:", storeId);
            const codes = await getPromoCodes(storeId);
            // console.log("Loaded promo codes:", codes);

            setPromoCodes(codes);
        } catch (err) {
            console.error("Error loading promo codes:", err);
            setError(`Failed to load promo codes: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        // console.log("Resetting form");
        setFormData({
            code: "",
            description: "",
            discountType: "percentage",
            discountValue: "",
            minimumOrderAmount: "",
            maximumDiscountAmount: "",
            usageLimit: "",
            isActive: true,
            validFrom: "",
            validUntil: "",
            applicableProducts: [],
            applicableCategories: [],
            firstTimeOnly: false
        });
        setEditingCode(null);
        setShowCreateForm(false);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    // FIXED: Prevent form submission reload
    const handleSubmit = async (e) => {
        e.preventDefault(); // This prevents the default form submission
        e.stopPropagation(); // This prevents event bubbling

        // console.log("🚀 Form submission started - NO RELOAD");
        // console.log("Form data:", formData);
        // console.log("Store ID:", storeId);

        if (!storeId) {
            // console.error("❌ No store ID provided");
            setError(t('storeIdRequired'));
            return;
        }

        setCreating(true);
        setError("");
        setSuccess("");

        try {
            // Validate required fields
            if (!formData.code.trim()) {
                throw new Error(t('promoCodeRequired'));
            }

            if (!formData.discountValue || parseFloat(formData.discountValue) <= 0) {
                throw new Error(t('discountValueMustBeGreaterThanZero'));
            }

            // Prepare data for submission
            const submitData = {
                code: formData.code.trim().toUpperCase(),
                description: formData.description.trim(),
                discountType: formData.discountType,
                discountValue: parseFloat(formData.discountValue) || 0,
                minimumOrderAmount: parseFloat(formData.minimumOrderAmount) || 0,
                maximumDiscountAmount: parseFloat(formData.maximumDiscountAmount) || 0,
                usageLimit: parseInt(formData.usageLimit) || 0,
                isActive: formData.isActive,
                firstTimeOnly: formData.firstTimeOnly,
                validFrom: formData.validFrom ? new Date(formData.validFrom) : new Date(),
                validUntil: formData.validUntil ? new Date(formData.validUntil) : null,
                applicableProducts: formData.applicableProducts || [],
                applicableCategories: formData.applicableCategories || []
            };

            // console.log("✅ Prepared submit data:", submitData);

            if (editingCode) {
                // Update existing promo code
                // console.log("📝 Updating promo code:", editingCode.id);
                await updatePromoCode(storeId, editingCode.id, submitData);
                // console.log("✅ Update successful");
                setSuccess(t('promoCodeUpdatedSuccessfully'));
            } else {
                // Create new promo code
                // console.log("🆕 Creating new promo code");
                // console.log("Calling createPromoCode with:", { storeId, submitData });

                const newId = await createPromoCode(storeId, submitData);
                // console.log("✅ Creation successful, new ID:", newId);
                setSuccess(t('promoCodeCreatedSuccessfully'));
            }

            resetForm();
            // console.log("🔄 Reloading promo codes list");
            await loadPromoCodes();
        } catch (err) {
            console.error("❌ Error during promo code operation:", err);
            console.error("Error details:", {
                message: err.message,
                stack: err.stack,
                name: err.name,
                code: err.code
            });
            setError(err.message || t('failedToSavePromoCode'));
        } finally {
            setCreating(false);
            // console.log("🏁 Form submission completed");
        }
    };

    const handleToggleForm = (e) => {
        // Prevent all forms of event propagation and default behavior
        e.preventDefault();
        e.stopPropagation();


        // console.log("🔘 Toggle form button clicked, current state:", showCreateForm);
        setShowCreateForm(!showCreateForm);

        // Return false as an extra precaution
        return false;
    };



    const handleEdit = (promoCode) => {
        // console.log("✏️ Editing promo code:", promoCode);

        setFormData({
            code: promoCode.code,
            description: promoCode.description || "",
            discountType: promoCode.discountType,
            discountValue: promoCode.discountValue.toString(),
            minimumOrderAmount: promoCode.minimumOrderAmount.toString(),
            maximumDiscountAmount: promoCode.maximumDiscountAmount.toString(),
            usageLimit: promoCode.usageLimit.toString(),
            isActive: promoCode.isActive,
            validFrom: promoCode.validFrom ? promoCode.validFrom.toISOString().split('T')[0] : "",
            validUntil: promoCode.validUntil ? promoCode.validUntil.toISOString().split('T')[0] : "",
            applicableProducts: promoCode.applicableProducts || [],
            applicableCategories: promoCode.applicableCategories || [],
            firstTimeOnly: promoCode.firstTimeOnly || false
        });
        setEditingCode(promoCode);
        setShowCreateForm(true);
    };

    const handleDelete = async (promoCodeId) => {
        if (!window.confirm("Are you sure you want to delete this promo code?")) {
            return;
        }

        try {
            // console.log("🗑️ Deleting promo code:", promoCodeId);
            await deletePromoCode(storeId, promoCodeId);
            // console.log("✅ Delete successful");
            setSuccess(t('promoCodeDeletedSuccessfully'));
            await loadPromoCodes();
        } catch (err) {
            // console.error("❌ Error deleting promo code:", err);
            setError(t('failedToDeletePromoCode'));
        }
    };

    const handleToggleStatus = async (promoCode) => {
        try {
            // console.log("🔄 Toggling status for:", promoCode.id, "to:", !promoCode.isActive);
            await togglePromoCodeStatus(storeId, promoCode.id, !promoCode.isActive);
            // console.log("✅ Status toggle successful");
            setSuccess(t('promoCodeStatusUpdated').replace('{status}', !promoCode.isActive ? t('activated') : t('deactivated')));
            await loadPromoCodes();
        } catch (err) {
            console.error("❌ Error toggling status:", err);
            setError(t('failedToUpdatePromoCodeStatus'));
        }
    };

    // Handle saving the promoCodesEnabled setting
    const handleSavePromoCodesEnabled = async () => {
        // Validate storeId
        if (!storeId) {
            console.error("❌ PromoCodesTab: storeId is undefined!");
            setError('Store ID is not configured. Please refresh the page.');
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            console.log("📤 PromoCodesTab - Saving for storeId:", storeId);
            await updateStoreSettings(storeId, {
                ...storeData,
                promoCodesEnabled: storeData?.promoCodesEnabled || false
            });
            setSuccess(t('settingsSavedSuccessfully') || 'Settings saved successfully');
        } catch (err) {
            console.error("❌ Error saving promo codes setting:", err);
            setError((t('failedToSaveSettings') || 'Failed to save settings') + ': ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return t('noExpiry');
        return new Date(date).toLocaleDateString();
    };

    const getDiscountDisplay = (promoCode) => {
        if (promoCode.discountType === "percentage") {
            return `${promoCode.discountValue}%`;
        } else {
            return `${promoCode.discountValue} AED`;
        }
    };

    if (loading) {
        return (
            <div className="promo-codes-loading">
                <div className="loader"></div>
                <p>{t('loadingPromoCodes')}</p>
                <small style={{ color: '#666', fontSize: '12px' }}>
                    Store ID: {storeId || 'Not provided'}
                </small>
            </div>
        );
    }

    return (
        <div className="promo-codes-tab">
            {/* Enable/Disable Promo Codes Setting */}
            <div className="promo-codes-setting">
                <div className="form-checkbox">
                    <label htmlFor="promoCodesEnabled" className="checkbox-label">
                        <input
                            type="checkbox"
                            id="promoCodesEnabled"
                            name="promoCodesEnabled"
                            checked={storeData?.promoCodesEnabled || false}
                            onChange={handleChange}
                        />
                        <span className="checkmark"></span>
                        <span className="label-text">{t('enablePromoCodes')}</span>
                    </label>
                    <p className="form-help">
                        {t('promoCodesDescription')}
                    </p>
                </div>
                <button
                    type="button"
                    className="admin-primary-btn"
                    onClick={handleSavePromoCodesEnabled}
                    disabled={saving}
                    style={{ marginTop: '10px' }}
                >
                    {saving ? (t('saving') || 'Saving...') : (t('saveChanges') || 'Save Changes')}
                </button>
            </div>

            {/* Only show promo codes management if promo codes are enabled */}
            {storeData?.promoCodesEnabled && (
                <>
                    <div className="promo-codes-header">
                        <h3>{t('managePromoCodes')}</h3>
                        {/* FIXED: Added type="button" to prevent form submission */}
                        <button
                            type="button"
                            className="admin-primary-btn"
                            onClick={handleToggleForm}
                            onMouseDown={(e) => e.preventDefault()}
                            disabled={!storeId}
                        >
                            {showCreateForm ? t('cancel') : t('createNewPromoCode')}
                        </button>
                    </div>

                    {error && <div className="admin-error-message">{error}</div>}
                    {success && <div className="admin-success-message">{success}</div>}

                    {!storeId && (
                        <div className="admin-error-message">
                            {t('storeIdRequired')}
                        </div>
                    )}

                    {/* Create/Edit Form - REMOVED from parent form */}
                    {showCreateForm && storeId && (
                        <div className="promo-code-form-container">
                            <h4>{editingCode ? t('editPromoCode') : t('createNewPromoCode')}</h4>
                            {/* This form is now independent */}
                            <form onSubmit={handleSubmit} className="promo-code-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="code">{t('promoCode')} *</label>
                                        <input
                                            type="text"
                                            id="code"
                                            name="code"
                                            value={formData.code}
                                            onChange={handleInputChange}
                                            placeholder={t('promoCodePlaceholder')}
                                            required
                                            maxLength="50"
                                            style={{ textTransform: "uppercase" }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="description">{t('description')}</label>
                                        <input
                                            type="text"
                                            id="description"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder={t('descriptionPlaceholder')}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="discountType">{t('discountType')} *</label>
                                        <select
                                            id="discountType"
                                            name="discountType"
                                            value={formData.discountType}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="percentage">{t('percentage')}</option>
                                            <option value="fixed">{t('fixedAmount')}</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="discountValue">
                                            {t('discountValue')} * {formData.discountType === "percentage" ? "(%)" : "(AED)"}
                                        </label>
                                        <input
                                            type="number"
                                            id="discountValue"
                                            name="discountValue"
                                            value={formData.discountValue}
                                            onChange={handleInputChange}
                                            placeholder={formData.discountType === "percentage" ? "20" : "50"}
                                            required
                                            min="0"
                                            max={formData.discountType === "percentage" ? "100" : undefined}
                                            step="0.01"
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="minimumOrderAmount">{t('minimumOrderAmount')}</label>
                                        <input
                                            type="number"
                                            id="minimumOrderAmount"
                                            name="minimumOrderAmount"
                                            value={formData.minimumOrderAmount}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="maximumDiscountAmount">{t('maximumDiscountAmount')}</label>
                                        <input
                                            type="number"
                                            id="maximumDiscountAmount"
                                            name="maximumDiscountAmount"
                                            value={formData.maximumDiscountAmount}
                                            onChange={handleInputChange}
                                            placeholder={`0 for ${t('unlimited')}`}
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="usageLimit">{t('usageLimit')}</label>
                                        <input
                                            type="number"
                                            id="usageLimit"
                                            name="usageLimit"
                                            value={formData.usageLimit}
                                            onChange={handleInputChange}
                                            placeholder={`0 for ${t('unlimited')}`}
                                            min="0"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <div className="checkbox-group">
                                            <label htmlFor="isActive" className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    id="isActive"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleInputChange}
                                                />
                                                {t('active')}
                                            </label>
                                            <label htmlFor="firstTimeOnly" className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    id="firstTimeOnly"
                                                    name="firstTimeOnly"
                                                    checked={formData.firstTimeOnly}
                                                    onChange={handleInputChange}
                                                />
                                                {t('firstTimeOnly')}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="validFrom">{t('validFrom')}</label>
                                        <input
                                            type="date"
                                            id="validFrom"
                                            name="validFrom"
                                            value={formData.validFrom}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="validUntil">{t('validUntil')}</label>
                                        <input
                                            type="date"
                                            id="validUntil"
                                            name="validUntil"
                                            value={formData.validUntil}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-actions">
                                    {/* FIXED: Added type="button" to prevent form submission */}
                                    <button
                                        type="button"
                                        className="admin-secondary-btn"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            resetForm();
                                        }}
                                    >
                                        {t('cancel')}
                                    </button>
                                    {/* This submit button is correct */}
                                    <button
                                        type="submit"
                                        className="admin-primary-btn"
                                        disabled={creating}
                                    >
                                        {creating ? t('saving') : (editingCode ? t('editPromoCode') : t('createNewPromoCode'))}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Promo Codes List */}
                    <div className="promo-codes-list">
                        <h4>{t('managePromoCodes')} ({promoCodes.length})</h4>

                        {promoCodes.length === 0 ? (
                            <div className="no-promo-codes">
                                <p>{t('noPromoCodesFound')}</p>
                                {storeId && (
                                    <button
                                        type="button"
                                        className="admin-primary-btn"
                                        onClick={() => setShowCreateForm(true)}
                                    >
                                        {t('createFirstPromoCode')}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="promo-codes-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t('code')}</th>
                                            <th>{t('value')}</th>
                                            <th>{t('minimumOrderAmount')}</th>
                                            <th>{t('usageLimit')}</th>
                                            <th>{t('validUntil')}</th>
                                            <th>{t('status')}</th>
                                            <th>{t('actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {promoCodes.map((code) => (
                                            <tr key={code.id} className={!code.isActive ? "inactive" : ""}>
                                                <td>
                                                    <div className="code-info">
                                                        <strong>{code.code}</strong>
                                                        {code.description && <small>{code.description}</small>}
                                                    </div>
                                                </td>
                                                <td>{getDiscountDisplay(code)}</td>
                                                <td>{code.minimumOrderAmount > 0 ? `${code.minimumOrderAmount} AED` : "No minimum"}</td>
                                                <td>
                                                    {code.usageLimit > 0
                                                        ? `${code.usedCount}/${code.usageLimit}`
                                                        : `${code.usedCount}/∞`}
                                                </td>
                                                <td>{formatDate(code.validUntil)}</td>
                                                <td>
                                                    <span className={`status-badge ${code.isActive ? "active" : "inactive"}`}>
                                                        {code.isActive ? t('active') : t('inactive')}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            type="button"
                                                            className="action-btn edit"
                                                            onClick={() => handleEdit(code)}
                                                            title={t('edit')}
                                                        >
                                                            ✏️ {t('edit')}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className={`action-btn ${code.isActive ? "deactivate" : "activate"}`}
                                                            onClick={() => handleToggleStatus(code)}
                                                            title={code.isActive ? t('deactivate') : t('activate')}
                                                        >
                                                            {code.isActive ? t('deactivate') : `✅ ${t('activate')}`}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="action-btn delete"
                                                            onClick={() => handleDelete(code.id)}
                                                            title={t('delete')}
                                                        >
                                                            🗑 {t('delete')}
                                                        </button>
                                                    </div>

                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Show message when promo codes are disabled */}
            {!storeData?.promoCodesEnabled && (
                <div className="promo-codes-disabled">
                    <h4>Promo Codes are Disabled</h4>
                    <p>Enable promo codes above to start creating and managing discount codes for your store.</p>
                </div>
            )}
        </div>
    );
};

export default PromoCodesTab;